import { Box, InputBase } from '@mui/material';
import React, { ChangeEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import { StyledButtonGroup } from '../RichTextEditor/RichTextEditor.style';
import AttachmentIconButton from './attachmentIconButton';
import AttachmentList from './attachmentList';
import { StyledCommentInput } from './commentInput.style';
import SubmitIconButton from './submitIconButton';
import { AttachmentConfig, CommentValue } from '../comments.model';
import GeneralDialog from 'component/dialogComponents/generalDialog/generalDialog';
import HlxButton from 'component/commonComponents/button/button';
import { attachmentValidator } from '../utils';

const RICH_TEXT = true;
const MEGABYTES = 1024 * 1024;

const defaultAttachmentConfig = {
  acceptFiles: {
    'image/jpeg': [],
    'image/png': [],
    'text/plain': ['.txt'],
    'application/pdf': [],
  },
  maxSize: 20,
  maxFiles: 3,
  disabled: false,
  canDelete: true,
};

interface CommentInputProps {
  isReplyComment?: boolean;
  value: string;
  attachments?: File[];
  attachmentConfig?: AttachmentConfig;
  placeholder?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onAttachmentChange?: (files: File[]) => void;
  onKeyPress?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onSubmit?: (value: CommentValue) => void;
  setIsOpenAttachment?: React.Dispatch<React.SetStateAction<boolean>>;
  isOpenAttachment?: boolean;
  locale?: any;
  loading?: boolean;
}

const CommentInput = (props: CommentInputProps) => {
  const {
    isReplyComment = false,
    value,
    attachments = [],
    attachmentConfig = {},
    onChange,
    onAttachmentChange,
    onKeyPress,
    onSubmit,
    placeholder,
    setIsOpenAttachment,
    isOpenAttachment,
    locale = {},
    loading
  } = props;

  const attachmentsRef = useRef(attachments);
  const [error, setError] = useState<string>('');
  const [attachmentsData, setAttachmentsData] = useState<File[]>([]);

  const { acceptFiles, maxSize, maxFiles, disabled, canDelete } = {
    ...defaultAttachmentConfig,
    ...attachmentConfig,
  };

  const getAttachmentsConfig = useCallback(() => {
    const config = isReplyComment
      ? { attachments: attachmentsData, setAttachments: setAttachmentsData }
      : { attachments, setAttachments: onAttachmentChange };
    return config;
  }, [attachments, attachmentsData, isReplyComment, onAttachmentChange]);

  const disabledAttachment = useMemo(() => {
    const { attachments } = getAttachmentsConfig();
    return disabled || attachments.length >= maxFiles;
  }, [disabled, getAttachmentsConfig, maxFiles]);

  const dropzoneState = useDropzone({
    accept: acceptFiles,
    onDrop: (files: File[]) => {
      if (isReplyComment) {
        setAttachmentsData(previousAttachments => {
          const updatedAttachments = [...previousAttachments, ...files].slice(0, maxFiles);
          return updatedAttachments;
        });
        return;
      }
      onAttachmentChange?.([...attachments, ...files].slice(0, maxFiles));
    },
    maxSize: maxSize * MEGABYTES,
    maxFiles: maxFiles,
    disabled: disabledAttachment,
    onDropRejected(fileRejections) {
      const errorMessage =
        fileRejections?.[0]?.errors?.[1]?.code === 'file-too-large'
          ? `${locale?.uploadAttachment?.errMsg[fileRejections?.[0]?.errors?.[1]?.code]} ${maxFiles}MB.` ||
            fileRejections?.[0]?.errors?.[1]?.message
          : locale?.uploadAttachment?.errMsg[fileRejections?.[0]?.errors?.[1]?.code] ||
            fileRejections?.[0]?.errors?.[1]?.message ||
            '';
      setError(errorMessage);
    },
    validator: attachmentValidator,
  });

  const closeDialog = () => setError('');

  const generateChangeEvent = (value: string) => {
    return { target: { value } } as ChangeEvent<HTMLInputElement>;
  };

  const removeAttachment = (index: number) => {
    const { attachments, setAttachments } = getAttachmentsConfig();
    const newAttachments = attachments.slice(0, index).concat(attachments.slice(index + 1));
    setAttachments?.(newAttachments);
  };

  const handleSubmit = (refValue?: string) => {
    const { setAttachments } = getAttachmentsConfig();
    onSubmit?.({ content: refValue ?? value, attachments: attachmentsRef.current });
    setAttachments?.([]);
  };

const renderAttachmentList = () => {
  const { attachments: attachmentDataToRender } = getAttachmentsConfig();

  if (attachmentDataToRender.length > 0) {
    return (
      <AttachmentList
        attachments={attachmentDataToRender}
        canDelete={canDelete}
        onRemove={removeAttachment}
      />
    );
  }

  return null;
};

  useEffect(() => {
    const { attachments } = getAttachmentsConfig();
    attachmentsRef.current = attachments;
  }, [attachments, attachmentsData, getAttachmentsConfig]);

  return (
    <Box>
      <StyledCommentInput>
        {RICH_TEXT ? (
          <RichTextEditor
            value={value}
            placeholder={placeholder}
            onChange={(html) => onChange?.(generateChangeEvent(html))}
            onKeyPress={onKeyPress}
            onSubmit={handleSubmit}
            dropzoneState={dropzoneState}
            disabledAttachment={disabledAttachment}
            setIsOpenAttachment={setIsOpenAttachment}
            isOpenAttachment={isOpenAttachment}
            loading={loading}
          />
        ) : (
          <Box position="relative">
            <InputBase
              className="inputBase"
              placeholder={placeholder}
              inputProps={{ 'aria-label': placeholder }}
              value={value}
              onKeyDown={onKeyPress}
              onChange={onChange}
              id="txt-comment-input"
            />

            <StyledButtonGroup>
              <AttachmentIconButton tabIndex={0} dropzoneState={dropzoneState} disabled={disabledAttachment} setIsOpenAttachment={setIsOpenAttachment} isOpenAttachment={isOpenAttachment}/>
              <SubmitIconButton tabIndex={0} disabled={!value} onClick={handleSubmit} id="btn-comment-submit" />
            </StyledButtonGroup>
          </Box>
        )}

        {renderAttachmentList()}
      </StyledCommentInput>

      <GeneralDialog
        title={locale?.uploadAttachment?.errMsg?.error || 'Error'}
        content={error}
        isOpen={!!error}
        buttonSubmit={<HlxButton label={locale?.uploadAttachment?.errMsg?.ok || 'Ok'} onClick={closeDialog} />}
        deviders={false}
        headerBackgroundDefault={false}
        hideButtonCancel={true}
        hideCloseIcon={true}
        commonStyles={{ width: '480px' }}
        handleClose={closeDialog}
      />
    </Box>
  );
};

export default CommentInput;
